import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Loader2, Lock, Sparkles, PlayCircle, Flame, Package, X, ShieldAlert, Search } from 'lucide-react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import SmartImage from '../components/SmartImage';
import ShareButton from '../components/ShareButton';
import { MediaBadges, CategoryBadge } from '../components/MediaBadges';
import api, { errText } from '../lib/api';
import { useToast } from '../hooks/use-toast';
import { useAuth } from '../context/AuthContext';

const DURATION_BUCKETS = [
  { value: 'lte5', label: '5 minutes or less', test: (d) => d <= 5 },
  { value: '10', label: '~10 minutes', test: (d) => d > 5 && d <= 12 },
  { value: '15', label: '~15 minutes', test: (d) => d > 12 && d <= 22 },
  { value: '30', label: '~30 minutes', test: (d) => d > 22 && d <= 37 },
  { value: '45', label: '~45 minutes', test: (d) => d > 37 && d <= 52 },
  { value: '60', label: '~1 hour', test: (d) => d > 52 },
];
const DURATION_BUCKET_MAP = Object.fromEntries(DURATION_BUCKETS.map((b) => [b.value, b]));
const SETUP_LABEL = { private_space: 'Private space', public_place: 'Public place', outdoors: 'Outdoors', anywhere: 'Anywhere' };
const durationLabel = (m) => {
  m = Math.round(m || 0);
  const h = Math.floor(m / 60), mins = m % 60, parts = [];
  if (h) parts.push(`${h} hour${h > 1 ? 's' : ''}`);
  if (mins) parts.push(`${mins} minute${mins > 1 ? 's' : ''}`);
  return parts.join(' ') || '0 minutes';
};

const FilterSelect = ({ label, value, onChange, options, testid }) => (
  <label className="flex flex-col gap-1">
    <span className="text-[11px] uppercase tracking-widest text-mahogany/60">{label}</span>
    <select value={value} onChange={(e) => onChange(e.target.value)} data-testid={testid}
      className="bg-white rounded-full border border-rose/50 px-3.5 py-2 text-sm text-mahogany focus:outline-none capitalize min-w-[130px]">
      {options.map((o) => <option key={o.value} value={o.value} className="capitalize">{o.label}</option>)}
    </select>
  </label>
);

const BundleModal = ({ bundle, disclaimer, onClose }) => {
  const { toast } = useToast();
  const [agreed, setAgreed] = useState(false);
  const [busy, setBusy] = useState(false);
  const buy = async () => {
    if (!agreed) return toast({ title: 'Please agree to the disclaimer first' });
    setBusy(true);
    try {
      const r = await api.post(`/meditations/bundles/${bundle.id}/checkout`, { origin_url: window.location.origin, disclaimer_agreed: true });
      if (r.data.checkout_url) window.location.href = r.data.checkout_url;
    } catch (e) { toast({ title: 'Checkout failed', description: errText(e) }); setBusy(false); }
  };
  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" data-testid="bundle-modal">
      <div className="bg-cream rounded-3xl p-6 max-w-lg w-full max-h-[85vh] overflow-y-auto">
        <div className="flex items-center justify-between mb-3"><h2 className="font-serif-display text-2xl text-burgundy flex items-center gap-2"><Package className="w-5 h-5" /> {bundle.title}</h2><button onClick={onClose}><X className="w-5 h-5 text-mahogany/70" /></button></div>
        {bundle.description && <p className="text-sm text-mahogany/80">{bundle.description}</p>}
        <p className="mt-2 text-sm text-mahogany/70">Unlocks all <strong>{bundle.technique_count}</strong> techniques in <strong>{bundle.purpose}</strong> — permanently.</p>
        <div className="mt-4 text-left rounded-2xl bg-rose-soft/30 border border-rose/40 p-4">
          <div className="flex items-center gap-2 text-burgundy font-medium mb-2 text-sm"><ShieldAlert className="w-4 h-4" /> Health &amp; safety disclaimer</div>
          <p className="text-xs text-mahogany/75 leading-relaxed max-h-32 overflow-y-auto pr-1">{disclaimer}</p>
          <label className="mt-3 flex items-start gap-2 text-sm text-mahogany cursor-pointer">
            <input type="checkbox" checked={agreed} onChange={(e) => setAgreed(e.target.checked)} className="mt-0.5" data-testid="bundle-disclaimer-agree" />
            <span>I have read, understood and agree. I practise at my own risk.</span>
          </label>
        </div>
        <button onClick={buy} disabled={busy || !agreed} className="pill-btn mt-4 w-full justify-center disabled:opacity-50" data-testid="bundle-buy-btn">
          {busy && <Loader2 className="w-4 h-4 animate-spin" />}
          Unlock all for {bundle.discount_price != null && <span className="line-through opacity-60 mx-1">${bundle.price}</span>}${bundle.effective_price}
        </button>
      </div>
    </div>
  );
};

const Meditations = () => {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [progress, setProgress] = useState(null);
  const [bundles, setBundles] = useState([]);
  const [openBundle, setOpenBundle] = useState(null);
  const [fPurpose, setFPurpose] = useState('all');
  const [fCategory, setFCategory] = useState('all');
  const [fTime, setFTime] = useState('all');
  const [fDuration, setFDuration] = useState('all');
  const [fSetup, setFSetup] = useState('all');
  const [fMedia, setFMedia] = useState('all');
  const [fSearch, setFSearch] = useState('');

  useEffect(() => {
    api.get('/meditations').then((r) => setData(r.data)).catch(() => setData({ groups: [] })).finally(() => setLoading(false));
    api.get('/meditations/bundles').then((r) => setBundles(r.data || [])).catch(() => setBundles([]));
    if (user) api.get('/meditations/progress').then((r) => setProgress(r.data)).catch(() => {});
  }, [user]);

  const bundleFor = (purpose) => bundles.find((b) => b.purpose === purpose && !b.fully_owned);
  const techPurposes = (t) => (t.purposes && t.purposes.length ? t.purposes : (t.purpose ? [t.purpose] : []));
  const techCategories = (t) => (t.categories && t.categories.length ? t.categories : (t.category ? [t.category] : []));

  // Flatten all purpose groups into one list, de-duplicated by id — a technique can
  // serve several purposes but must render exactly once.
  const seenIds = new Set();
  const allTechs = (data?.groups || []).flatMap((g) => g.techniques).filter((t) => {
    if (seenIds.has(t.id)) return false;
    seenIds.add(t.id); return true;
  });
  const uniq = (arr) => [...new Set(arr.filter(Boolean))];
  const purposeOpts = [{ value: 'all', label: 'All purposes' }, ...uniq(allTechs.flatMap(techPurposes)).map((p) => ({ value: p, label: p }))];
  const categoryOpts = [{ value: 'all', label: 'All categories' }, { value: 'Active', label: 'Active' }, { value: 'Passive', label: 'Passive' }];
  const TIME_ORDER = ['dawn', 'morning', 'afternoon', 'evening', 'night', 'anytime'];
  const timeOpts = [{ value: 'all', label: 'Any time' }, ...uniq(allTechs.map((t) => t.time_of_day)).sort((a, b) => TIME_ORDER.indexOf(a) - TIME_ORDER.indexOf(b)).map((t) => ({ value: t, label: t }))];
  const durationOpts = [{ value: 'all', label: 'Any length' }, ...DURATION_BUCKETS.filter((b) => allTechs.some((t) => (t.durations || []).some(b.test))).map((b) => ({ value: b.value, label: b.label }))];
  const setupPresent = uniq(allTechs.flatMap((t) => t.setup || []));
  const setupOpts = [{ value: 'all', label: 'Anywhere' }, ...setupPresent.map((s) => ({ value: s, label: SETUP_LABEL[s] || s }))];
  const mediaOpts = [{ value: 'all', label: 'Any media' }, { value: 'music', label: 'Has music' }, { value: 'video', label: 'Has video' }, { value: 'audio', label: 'Has guided audio' }];

  const match = (t) => (fPurpose === 'all' || techPurposes(t).includes(fPurpose))
    && (fCategory === 'all' || techCategories(t).includes(fCategory))
    && (fTime === 'all' || t.time_of_day === fTime)
    && (fDuration === 'all' || (DURATION_BUCKET_MAP[fDuration] && (t.durations || []).some(DURATION_BUCKET_MAP[fDuration].test)))
    && (fSetup === 'all' || (t.setup || []).includes(fSetup))
    && (fMedia === 'all' || (fMedia === 'music' && t.has_music) || (fMedia === 'video' && t.has_video) || (fMedia === 'audio' && t.has_audio))
    && (!fSearch.trim() || (t.title || '').toLowerCase().includes(fSearch.trim().toLowerCase()));

  const filtered = allTechs.filter(match);
  const anyFilter = [fPurpose, fCategory, fTime, fDuration, fSetup, fMedia].some((v) => v !== 'all') || !!fSearch.trim();
  const clearFilters = () => { setFPurpose('all'); setFCategory('all'); setFTime('all'); setFDuration('all'); setFSetup('all'); setFMedia('all'); setFSearch(''); };
  // Bundle offers: focus on the selected purpose, else show every purpose with an available bundle.
  const bundleStrip = fPurpose !== 'all' ? [bundleFor(fPurpose)].filter(Boolean) : bundles.filter((b) => !b.fully_owned);

  if (loading) return <div className="min-h-screen bg-cream"><Header /><div className="min-h-[60vh] flex items-center justify-center"><Loader2 className="w-6 h-6 animate-spin text-mahogany" /></div><Footer /></div>;

  return (
    <div className="min-h-screen bg-cream" data-testid="meditations-page">
      <Header />
      <div className="max-w-6xl mx-auto px-5 py-16">
        <div className="max-w-2xl">
          <div className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-rose-italic mb-3"><Sparkles className="w-4 h-4" /> Members' Meditations</div>
          <h1 className="font-serif-display text-4xl sm:text-5xl text-burgundy">Purpose-driven <span className="font-italic-display text-rose-italic">meditation techniques</span></h1>
          <p className="mt-3 text-mahogany/80">Guided techniques for what you need right now — stress, sleep, focus, letting go. Basic members enjoy {data?.free_limit ?? 2} free; unlock the rest with a Meditators membership.</p>
          {data?.free_remaining > 0 && <p className="mt-2 text-sm text-green-700 font-medium" data-testid="free-remaining">You have {data.free_remaining} free technique{data.free_remaining > 1 ? 's' : ''} left to unlock.</p>}
          {progress && progress.total_sessions > 0 && (
            <div className="mt-4 inline-flex items-center gap-4 rounded-2xl bg-white border border-rose/40 px-4 py-2.5 text-sm" data-testid="progress-badge">
              <span className="inline-flex items-center gap-1.5 font-medium text-burgundy"><Flame className="w-4 h-4 text-rose-italic" /> {progress.streak}-day streak</span>
              <span className="text-mahogany/70">{progress.total_sessions} sessions · {progress.techniques_practiced} techniques</span>
            </div>
          )}
        </div>

        <div className="mt-8 flex flex-col gap-3 p-4 rounded-3xl bg-white/60 border border-rose/30" data-testid="meditation-filters">
          <div className="relative w-full">
            <Search className="w-4 h-4 text-mahogany/50 absolute left-4 top-1/2 -translate-y-1/2 pointer-events-none" />
            <input
              type="text"
              value={fSearch}
              onChange={(e) => setFSearch(e.target.value)}
              placeholder="Search meditations by name…"
              data-testid="meditation-search"
              className="w-full bg-white rounded-full border border-rose/50 pl-11 pr-10 py-2.5 text-sm text-mahogany placeholder:text-mahogany/40 focus:outline-none focus:border-rose"
            />
            {fSearch && (
              <button onClick={() => setFSearch('')} className="absolute right-3 top-1/2 -translate-y-1/2 text-mahogany/50 hover:text-burgundy" data-testid="meditation-search-clear" aria-label="Clear search">
                <X className="w-4 h-4" />
              </button>
            )}
          </div>
          <div className="flex flex-wrap items-end gap-3">
            <FilterSelect label="Purpose" value={fPurpose} onChange={setFPurpose} options={purposeOpts} testid="filter-purpose" />
            <FilterSelect label="Category" value={fCategory} onChange={setFCategory} options={categoryOpts} testid="filter-category" />
            <FilterSelect label="Time of day" value={fTime} onChange={setFTime} options={timeOpts} testid="filter-time" />
            <FilterSelect label="Duration" value={fDuration} onChange={setFDuration} options={durationOpts} testid="filter-duration" />
            <FilterSelect label="Setting" value={fSetup} onChange={setFSetup} options={setupOpts} testid="filter-setup" />
            <FilterSelect label="Media" value={fMedia} onChange={setFMedia} options={mediaOpts} testid="filter-media" />
            {anyFilter && <button onClick={clearFilters} className="text-sm text-rose-italic hover:text-burgundy underline underline-offset-2 pb-2" data-testid="clear-filters">Clear</button>}
          </div>
        </div>

        {bundleStrip.length > 0 && (
          <div className="mt-6 flex flex-wrap gap-2.5" data-testid="bundle-strip">
            {bundleStrip.map((b) => (
              <button key={b.id} onClick={() => setOpenBundle(b)} className="pill-btn-outline text-sm" data-testid={`bundle-cta-${b.purpose.replace(/\s+/g, '-').toLowerCase()}`}>
                <Package className="w-4 h-4" /> Unlock all {b.purpose} for {b.discount_price != null && <span className="line-through opacity-60 mx-1">${b.price}</span>}${b.effective_price}
              </button>
            ))}
          </div>
        )}

        <p className="mt-8 mb-4 text-sm text-mahogany/60" data-testid="results-count">
          {filtered.length} technique{filtered.length === 1 ? '' : 's'}{fPurpose !== 'all' ? ` for ${fPurpose}` : ''}
        </p>

        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5" data-testid="meditation-grid">
          {filtered.map((t) => (
            <Link key={t.id} to={`/meditations/${t.slug}`} data-testid={`meditation-card-${t.slug}`}
              className="group rounded-3xl overflow-hidden bg-white border border-rose/30 hover:border-rose/60 transition-colors shadow-sm hover:shadow-md flex flex-col">
              <div className="aspect-[16/10] bg-rose-soft/40 relative overflow-hidden">
                {t.hero_image
                  ? <SmartImage src={t.hero_image} alt={t.title} className="absolute inset-0 w-full h-full" imgClass="group-hover:scale-105 transition-transform duration-500" />
                  : <div className="w-full h-full flex items-center justify-center text-mahogany/30"><PlayCircle className="w-12 h-12" /></div>}
                <div className="absolute top-3 left-3 z-10">
                  <ShareButton compact url={`${window.location.origin}/meditations/${t.slug}`} title={`${t.title} — Osho Universal`} text={t.summary || `Explore ${t.title} on Osho Universal`} testid={`share-med-${t.slug}`} />
                </div>
                {t.locked
                  ? <span className="absolute top-3 right-3 inline-flex items-center gap-1 text-[11px] font-medium px-2.5 py-1 rounded-full bg-mahogany/85 text-cream" data-testid={`price-badge-${t.slug}`}><Lock className="w-3 h-3" /> {t.is_free ? 'Members' : (t.effective_price ? <>{t.discount_price != null && <span className="line-through opacity-60 mr-1">${t.price}</span>}${t.effective_price}</> : 'Premium')}</span>
                  : <span className="absolute top-3 right-3 text-[11px] font-medium px-2.5 py-1 rounded-full bg-green-600/90 text-white">Unlocked</span>}
              </div>
              <div className="p-5 flex flex-col flex-1">
                {techPurposes(t).length > 0 && (
                  <div className="flex flex-wrap gap-1.5 mb-2" data-testid={`card-purposes-${t.slug}`}>
                    {techPurposes(t).map((p) => (
                      <span key={p} className="text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full bg-burgundy/10 text-burgundy">{p}</span>
                    ))}
                  </div>
                )}
                <h3 className="font-serif-display text-xl text-burgundy">{t.title}</h3>
                <p className="mt-1.5 text-sm text-mahogany/70 line-clamp-2">{t.summary}</p>
                <div className="mt-auto pt-3 flex flex-wrap items-center gap-1.5 text-[11px]">
                  <CategoryBadge tech={t} />
                  {t.time_of_day && <span className="px-2 py-0.5 rounded-full bg-rose-soft/60 text-mahogany capitalize">{t.time_of_day}</span>}
                  {(t.durations || []).length > 0 && <span className="px-2 py-0.5 rounded-full bg-rose-soft/60 text-mahogany">{durationLabel(Math.min(...t.durations))}{t.durations.length > 1 ? '+' : ''}</span>}
                  {(t.setup || []).slice(0, 1).map((s) => <span key={s} className="px-2 py-0.5 rounded-full bg-rose-soft/60 text-mahogany">{SETUP_LABEL[s] || s}</span>)}
                  <MediaBadges tech={t} className="ml-auto" />
                </div>
              </div>
            </Link>
          ))}
        </div>
        {!filtered.length && (
          <p className="mt-16 text-mahogany/60" data-testid="no-results">
            {anyFilter ? <>No techniques match these filters. <button onClick={clearFilters} className="text-rose-italic underline">Clear filters</button></> : 'No techniques published yet — check back soon.'}
          </p>
        )}
      </div>
      {openBundle && <BundleModal bundle={openBundle} disclaimer={data?.disclaimer} onClose={() => setOpenBundle(null)} />}
      <Footer />
    </div>
  );
};

export default Meditations;
